import React, { useEffect, useRef, useState } from "react";
import { Env } from "../../shared";
import { FieldLabel, inputClass } from "./FieldLabel";
import { FoundUser, UserSearchMode, useUserSearch } from "./useUserSearch";

export type PickedUser = FoundUser;

const MODE_HINTS: Record<UserSearchMode, string> = {
  user_id: "matched by user id",
  phone_last4: "matched by last 4 phone digits",
  email: "matched by email",
  name: "matched by name",
};

const SEARCH_DEBOUNCE_MS = 350;
const MIN_TERM_LEN = 2;

function UserSummary({ user }: { user: PickedUser }) {
  const label = user.display_name || user.email || user.user_id || "unknown account";
  return (
    <div className="min-w-0">
      <p className="text-sm text-white truncate">{label}</p>
      <p className="text-xs text-gray-500 truncate">
        {[
          user.user_id ? `id ${user.user_id}` : "id resolved on save",
          user.email && user.email !== label ? user.email : null,
          user.phone_masked,
          user.account_status,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </div>
  );
}

/**
 * Picks an app user account. The admin site's first user-search component — the
 * referral-codes form binds a promoter code's payout owner with it.
 *
 * A single input routes itself by what was typed (user id / last-4 / email /
 * name); see `useUserSearch` for which of those hit the API and which match the
 * caller-supplied `directory`.
 *
 * An email with no directory match is a valid pick: `user_id` stays null and the
 * caller sends the address as `owner_email`, which the backend resolves to
 * exactly one live account (or rejects). The field says so rather than implying
 * a resolved account.
 */
export function UserPicker({
  env,
  value,
  onChange,
  label = "Promoter",
  required,
  directory = [],
  helpText,
}: {
  env: Env;
  value: PickedUser | null;
  onChange: (u: PickedUser | null) => void;
  label?: string;
  required?: boolean;
  /** Users the caller already knows about — the name/email match set. */
  directory?: FoundUser[];
  helpText?: string;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const { results, mode, loading, error, search, reset } = useUserSearch(env, directory);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // `search` closes over `directory`, which callers rebuild freely; hold it in a
  // ref so a new array identity cannot re-trigger the search effect.
  const searchRef = useRef(search);
  searchRef.current = search;
  const resetRef = useRef(reset);
  resetRef.current = reset;

  useEffect(() => {
    if (debounced.length < MIN_TERM_LEN) {
      resetRef.current();
      return;
    }
    searchRef.current(debounced);
  }, [debounced]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (value) {
    return (
      <div>
        <FieldLabel required={required}>{label}</FieldLabel>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0 px-3.5 py-2.5 bg-gray-800 border border-gray-700 rounded-xl">
            <UserSummary user={value} />
          </div>
          <button
            type="button"
            onClick={() => { onChange(null); setQuery(""); setDebounced(""); setOpen(true); }}
            className="px-3 py-2.5 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-xl hover:text-white hover:border-violet-500 transition-all shrink-0"
          >
            Change
          </button>
        </div>
        {value.is_flagged && (
          <p className="text-xs text-red-400 mt-1">⚠ This account is flagged for fraud.</p>
        )}
        {value.account_status && value.account_status !== "active" && (
          <p className="text-xs text-amber-400 mt-1">⚠ Account status is “{value.account_status}”.</p>
        )}
        {!value.user_id && (
          <p className="text-xs text-amber-400 mt-1">
            Resolved from the email on save — rejected if it matches no account, or more than one.
          </p>
        )}
      </div>
    );
  }

  const showDropdown = open && debounced.length >= MIN_TERM_LEN;

  return (
    <div className="relative" ref={containerRef}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <input
        type="text"
        value={query}
        placeholder="Name, email, user id, or last 4 phone digits"
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        className={inputClass}
      />
      {helpText && <p className="text-xs text-gray-500 mt-1">{helpText}</p>}
      {showDropdown && (
        <div className="absolute z-20 mt-1 w-full bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-h-64 overflow-y-auto">
          {loading && <p className="px-3.5 py-2.5 text-sm text-gray-500">Searching…</p>}
          {!loading && error && <p className="px-3.5 py-2.5 text-sm text-red-400">{error}</p>}
          {!loading && !error && results.length === 0 && (
            <p className="px-3.5 py-2.5 text-sm text-gray-500">
              No account matches. Try a full user id, an email, or the last 4 phone digits.
            </p>
          )}
          {!loading && !error && results.length > 0 && mode && (
            <p className="px-3.5 pt-2 text-xs text-gray-500">{MODE_HINTS[mode]}</p>
          )}
          {!loading && !error && results.map((u) => (
            <button
              key={u.user_id ?? `email:${u.email}`}
              type="button"
              onClick={() => { onChange(u); setOpen(false); }}
              className="w-full text-left px-3.5 py-2.5 hover:bg-gray-800 transition-colors"
            >
              {u.source === "unresolved_email" ? (
                <div>
                  <p className="text-sm text-white truncate">Use {u.email}</p>
                  <p className="text-xs text-gray-500">Resolved to an account on save</p>
                </div>
              ) : (
                <UserSummary user={u} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
